import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { USGReport, TestName } from '../../types/diagnosis';
import toast from 'react-hot-toast';

export default function USGReport() {
  const [reports, setReports] = useState<USGReport[]>([]);
  const [tests, setTests] = useState<TestName[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState<USGReport | null>(null);
  const [formData, setFormData] = useState({
    patient_id: '',
    test_id: '',
    report_date: new Date().toISOString().split('T')[0],
    findings: '',
    impression: '',
    recommendations: '',
    status: 'pending' as 'pending' | 'completed' | 'verified',
  });

  useEffect(() => {
    loadTests();
    loadReports();
  }, []);

  const loadTests = async () => {
    try {
      const { data, error } = await supabase
        .from('test_names')
        .select('*')
        .order('test_name', { ascending: true});

      if (error) throw error;
      setTests(data || []);
    } catch (error: any) {
      console.error('Error loading tests:', error);
      toast.error('Failed to load tests');
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('usg_reports')
        .select(`
          *,
          patient:patients(*),
          test:test_names(*)
        `)
        .order('report_date', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error: any) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load USG reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingReport) {
        const { error } = await supabase
          .from('usg_reports')
          .update(formData)
          .eq('id', editingReport.id);

        if (error) throw error;
        toast.success('Report updated successfully');
      } else {
        const { error } = await supabase
          .from('usg_reports')
          .insert([formData]);

        if (error) throw error;
        toast.success('Report added successfully');
      }

      setShowForm(false);
      setEditingReport(null);
      setFormData({
        patient_id: '',
        test_id: '',
        report_date: new Date().toISOString().split('T')[0],
        findings: '',
        impression: '',
        recommendations: '',
        status: 'pending',
      });
      loadReports();
    } catch (error: any) {
      console.error('Error saving report:', error);
      toast.error(error.message || 'Failed to save report');
    }
  };

  const handleVerify = async (id: string) => {
    try {
      const { error } = await supabase
        .from('usg_reports')
        .update({
          status: 'verified',
          verified_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
      toast.success('Report verified successfully');
      loadReports();
    } catch (error: any) {
      console.error('Error verifying report:', error);
      toast.error('Failed to verify report');
    }
  };

  const handleEdit = (report: USGReport) => {
    setEditingReport(report);
    setFormData({
      patient_id: report.patient_id,
      test_id: report.test_id,
      report_date: report.report_date,
      findings: report.findings || '',
      impression: report.impression || '',
      recommendations: report.recommendations || '',
      status: report.status,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this report?')) return;

    try {
      const { error } = await supabase
        .from('usg_reports')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Report deleted successfully');
      loadReports();
    } catch (error: any) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  const filteredReports = reports.filter((report) =>
    report.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.test?.test_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">USG Reports</h1>
        <p className="text-gray-600">Manage ultrasound reports</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingReport(null);
              setFormData({
                patient_id: '',
                test_id: '',
                report_date: new Date().toISOString().split('T')[0],
                findings: '',
                impression: '',
                recommendations: '',
                status: 'pending',
              });
            }}
            className="ml-4 flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Report
          </button>
        </div>

        {showForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">
              {editingReport ? 'Edit Report' : 'Add New Report'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Patient ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.patient_id}
                    onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter patient ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.test_id}
                    onChange={(e) => setFormData({ ...formData, test_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">--Select Test--</option>
                    {tests.map((test) => (
                      <option key={test.id} value={test.id}>
                        {test.test_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Report Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.report_date}
                    onChange={(e) => setFormData({ ...formData, report_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="verified">Verified</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Findings
                </label>
                <textarea
                  value={formData.findings}
                  onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Impression
                </label>
                <textarea
                  value={formData.impression}
                  onChange={(e) => setFormData({ ...formData, impression: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recommendations
                </label>
                <textarea
                  value={formData.recommendations}
                  onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingReport(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingReport ? 'Update' : 'Add'} Report
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No reports found
                  </td>
                </tr>
              ) : (
                filteredReports.map((report, index) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.patient?.name || report.patient_id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.test?.test_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(report.report_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        report.status === 'verified' ? 'bg-green-100 text-green-800' :
                        report.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {report.status !== 'verified' && (
                        <button
                          onClick={() => handleVerify(report.id)}
                          className="text-green-600 hover:text-green-900 mr-4"
                          title="Verify"
                        >
                          <CheckCircle className="w-4 h-4 inline" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(report)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(report.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
